"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { ChevronRight, ChevronDown, ArrowRight } from "lucide-react";
import { sendGAEvent } from "@next/third-parties/google";
import { NavigationMenuProps } from "@/components/header/types";
import { hasDropdownContent, getCategories, getStandaloneCategoryLinks, getStandaloneHeaders } from "@/components/header/nav-utils";
import NavLink from "@/components/header/shared/NavLink";

/**
 * Component for a simple single-pane dropdown list
 */
const SimpleDropdown = React.memo(t0 => {
  const $ = _c(7);
  const {
    items,
    onLinkClick
  } = t0;
  let t1;
  if ($[0] !== items || $[1] !== onLinkClick) {
    let t2;
    if ($[3] !== onLinkClick) {
      t2 = subItem => <NavLink key={subItem.id} href={subItem.url || "#"} className="px-6 py-3 font-outfit text-[15px] text-gray-600 hover:text-[#037a6a] hover:bg-gray-50 transition-all" onClick={() => {
        sendGAEvent("event", "navigation_item_clicked", {
          item_name: subItem.title,
          item_id: String(subItem.id)
        });
        onLinkClick?.();
      }}>{subItem.title}</NavLink>;
      $[3] = onLinkClick;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = items.map(t2);
    $[0] = items;
    $[1] = onLinkClick;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <>{t1}</>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
});
SimpleDropdown.displayName = "SimpleDropdown";

// Reusable Promo Card Component to avoid duplication
const PromoCard = React.memo(t0 => {
  const $ = _c(25);
  const {
    currentSubItem,
    isFullWidth: t1,
    onLinkClick
  } = t0;
  const isFullWidth = t1 === undefined ? false : t1;
  const t2 = `flex flex-col h-full rounded-[24px] overflow-hidden relative group/promo bg-[#F2F9F8] ${isFullWidth ? "w-full" : "w-[280px] flex-shrink-0"}`;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="absolute inset-0 bg-gradient-to-br from-[#037a6a]/5 to-transparent z-0" />;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const t4 = `relative z-10 flex flex-col justify-center items-start p-8 ${isFullWidth ? "w-full h-full p-10" : "w-full h-full"}`;
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="text-[10px] font-bold text-[#037a6a] uppercase tracking-[0.25em] mb-4">Featured Collection</span>;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const t6 = `font-plus_jakarta_sans font-semibold text-gray-900 mb-4 leading-tight ${isFullWidth ? "text-3xl" : "text-2xl"}`;
  const t7 = currentSubItem?.title;
  let t8;
  if ($[2] !== t6 || $[3] !== t7) {
    t8 = <div className={t6}>{t7}</div>;
    $[2] = t6;
    $[3] = t7;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const t9 = `text-gray-600 font-outfit text-[14px] leading-relaxed mb-6 ${isFullWidth ? "max-w-[400px] text-[15px] mb-8" : ""}`;
  const t10 = currentSubItem?.content || "Explore our handpicked selection of premium essentials. Every item is curated for quality and designed to make your celebrations truly special.";
  let t11;
  if ($[5] !== t10 || $[6] !== t9) {
    t11 = <p className={t9}>{t10}</p>;
    $[5] = t10;
    $[6] = t9;
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  const t12 = currentSubItem?.url || "#";
  let t13;
  if ($[8] !== currentSubItem?.id || $[9] !== currentSubItem?.title || $[10] !== onLinkClick) {
    t13 = () => {
      sendGAEvent("event", "navigation_item_clicked", {
        item_name: currentSubItem?.title,
        item_id: String(currentSubItem?.id)
      });
      onLinkClick?.();
    };
    $[8] = currentSubItem?.id;
    $[9] = currentSubItem?.title;
    $[10] = onLinkClick;
    $[11] = t13;
  } else {
    t13 = $[11];
  }
  let t14;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ArrowRight size={14} />;
    $[12] = t14;
  } else {
    t14 = $[12];
  }
  let t15;
  if ($[13] !== t12 || $[14] !== t13) {
    t15 = <NavLink href={t12} className="flex items-center gap-x-3 px-6 py-3 bg-[#037a6a] text-white rounded-full font-bold uppercase text-[10px] tracking-widest hover:bg-[#026658] hover:shadow-xl transition-all whitespace-nowrap" onClick={t13}>Shop Collection {t14}</NavLink>;
    $[13] = t12;
    $[14] = t13;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] !== t11 || $[17] !== t15 || $[18] !== t4 || $[19] !== t8) {
    t16 = <div className={t4}>{t5}{t8}{t11}{t15}</div>;
    $[16] = t11;
    $[17] = t15;
    $[18] = t4;
    $[19] = t8;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="absolute bottom-[-20%] right-[-10%] w-[200px] h-[200px] bg-[#037a6a]/10 rounded-full blur-3xl z-0" />;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== t16 || $[23] !== t2) {
    t18 = <div className={t2}>{t3}{t16}{t17}</div>;
    $[22] = t16;
    $[23] = t2;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  return t18;
});
PromoCard.displayName = "PromoCard";
const NavMenuItem = React.memo(t0 => {
  const $ = _c(54);
  const {
    item,
    navigation
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubId, setActiveSubId] = useState(null);
  let t1;
  if ($[0] !== item) {
    t1 = getCategories(item);
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const categories = t1;
  let t2;
  if ($[2] !== item) {
    t2 = hasDropdownContent(item);
    $[2] = item;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const shouldShowDropdown = t2;
  let t3;
  if ($[4] !== navigation) {
    t3 = navigation || [];
    $[4] = navigation;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = getStandaloneHeaders(t3);
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const standaloneHeaders = t4;
  let t5;
  if ($[8] !== item) {
    t5 = getStandaloneCategoryLinks(item);
    $[8] = item;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const standaloneLinks = t5;
  let t6;
  if ($[10] !== categories) {
    t6 = categories.flatMap(_temp);
    $[10] = categories;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const allSidebarItems = t6;
  let t7;
  if ($[12] !== item?.id || $[13] !== navigation) {
    t7 = () => (navigation || []).findIndex(n => n.id === item?.id);
    $[12] = item?.id;
    $[13] = navigation;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  item?.id;
  let t8;
  if ($[15] !== t7) {
    t8 = t7();
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const itemIndex = t8;
  if (!item?.id || !item?.title) {
    return null;
  }
  const isSpecialEdit = item.title?.trim().toUpperCase() === "FRIENDSHIP DAY EDIT" || item.title?.trim().toUpperCase() === "MEANINGFUL DIWALI";
  let t9;
  if ($[17] !== activeSubId || $[18] !== allSidebarItems) {
    t9 = allSidebarItems.find(sub => String(sub.id) === String(activeSubId)) || allSidebarItems[0];
    $[17] = activeSubId;
    $[18] = allSidebarItems;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const currentSubItem = t9;
  let t10;
  if ($[20] !== currentSubItem?.children) {
    t10 = currentSubItem?.children || [];
    $[20] = currentSubItem?.children;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const currentChildren = t10;
  const columnCount = currentChildren.length > 18 ? 3 : currentChildren.length > 9 ? 2 : 1;
  const dynamicRightWidth = currentChildren.length > 0 ? Math.max(columnCount * 260, 260) : 440;
  let t11;
  if ($[22] !== allSidebarItems[0]) {
    t11 = () => {
      setIsOpen(true);
      setActiveSubId(allSidebarItems[0] ? String(allSidebarItems[0].id) : null);
    };
    $[22] = allSidebarItems[0];
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      setIsOpen(false);
    };
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = `group-hover:text-[#037a6a] relative text-sm transition duration-300 py-1 md:py-2 lg:text-base lg:leading-5 flex items-center justify-center font-lora uppercase tracking-wider whitespace-nowrap ${isSpecialEdit ? "text-[#037a6a] font-bold" : `font-medium ${false ? "text-[#037a6a]" : "text-[#5A5A5A]"}`}`;
  let t14;
  if ($[25] !== item.id || $[26] !== item.title || $[27] !== item.url || $[28] !== shouldShowDropdown) {
    t14 = shouldShowDropdown ? <span onClick={e => {
      e.stopPropagation();
      setIsOpen(_temp2);
    }} className="flex items-center justify-center text-nowrap cursor-pointer">{item.title}{false && <motion.div layoutId={`activeNav-${item.id}`} className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#037a6a]" transition={{
        type: "spring",
        stiffness: 380,
        damping: 30
      }} />}</span> : <NavLink href={item.url} className="flex items-center justify-center w-full" onClick={() => {
      sendGAEvent("event", "navigation_item_clicked", {
        item_name: item.title,
        item_id: String(item.id)
      });
    }}>{item.title}{false && <motion.div layoutId={`activeNav-${item.id}`} className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#037a6a]" transition={{
        type: "spring",
        stiffness: 380,
        damping: 30
      }} />}</NavLink>;
    $[25] = item.id;
    $[26] = item.title;
    $[27] = item.url;
    $[28] = shouldShowDropdown;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== isOpen || $[31] !== shouldShowDropdown) {
    t15 = shouldShowDropdown && <ChevronDown className={`ml-1.5 w-3 h-3 transition-transform duration-300 ${isOpen ? "-rotate-180" : "rotate-0"}`} />;
    $[30] = isOpen;
    $[31] = shouldShowDropdown;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t13 || $[34] !== t14 || $[35] !== t15) {
    t16 = <div className={t13}>{t14}{t15}</div>;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== activeSubId || $[38] !== allSidebarItems || $[39] !== columnCount || $[40] !== currentChildren || $[41] !== currentSubItem || $[42] !== dynamicRightWidth || $[43] !== isOpen || $[44] !== item.id || $[45] !== itemIndex || $[46] !== shouldShowDropdown || $[47] !== standaloneHeaders || $[48] !== standaloneLinks) {
    t17 = shouldShowDropdown && <div className={`absolute z-40 pt-3 transition-all duration-500 ${isOpen ? "opacity-100 visible pointer-events-auto" : "opacity-0 invisible pointer-events-none"} ${itemIndex === 0 ? "left-0" : "left-1/2 -translate-x-1/2"}`} style={{
      width: "auto",
      maxWidth: "95vw"
    }}><div className="bg-white shadow-[0_40px_100px_rgba(0,0,0,0.18)] rounded-2xl border border-gray-100 overflow-hidden flex h-[450px]"><><div className="w-[260px] flex-shrink-0 bg-gray-50/80 border-r border-gray-100 py-4 flex flex-col overflow-y-auto no-scrollbar">{allSidebarItems.map((subItem_0, subIdx) => {
              const showHeader = subIdx === 0 || allSidebarItems[subIdx - 1]?.categoryType !== subItem_0.categoryType;
              const isAllLink = String(subItem_0.id).startsWith("all-");
              const subIdStr = String(subItem_0.id);
              const currentActiveSubIdStr = activeSubId ? String(activeSubId) : null;
              const isSubItemSelected = currentActiveSubIdStr ? currentActiveSubIdStr === subIdStr : subIdx === 0;
              return <React.Fragment key={subItem_0.id}>{showHeader && subItem_0.categoryTitle && <div className="px-6 text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-4 mb-2">{subItem_0.categoryTitle}</div>}<div className={`relative transition-all duration-200 ${isSubItemSelected ? "bg-white text-[#037a6a] shadow-sm z-10 font-bold" : "text-gray-600 hover:bg-gray-100/50"}`} onMouseEnter={() => setActiveSubId(subIdStr)}><NavLink href={subItem_0.url || "#"} className={`flex items-center justify-between px-6 py-4 font-outfit text-[14px] w-full ${isAllLink ? "font-bold" : "font-semibold"} cursor-pointer`} onClick={() => {
                    sendGAEvent("event", "navigation_item_clicked", {
                      item_name: subItem_0.title,
                      item_id: String(subItem_0.id)
                    });
                    setIsOpen(false);
                  }}><span className="truncate pr-2">{subItem_0.title}</span>{subItem_0.children?.length > 0 && <ChevronRight size={14} className={`transition-transform duration-200 flex-shrink-0 ${isSubItemSelected ? "translate-x-1 text-[#037a6a]" : "text-gray-400"}`} />}</NavLink>{isSubItemSelected && <motion.div layoutId={`activeSubNav-${item.id}`} className="absolute left-0 top-0 bottom-0 w-1 bg-[#037a6a]" />}</div></React.Fragment>;
            })}{(standaloneHeaders.length > 0 || standaloneLinks.length > 0) && <div className="mt-auto border-t border-gray-100 pt-4 pb-2"><div className="px-6 text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">Explore</div>{([...standaloneHeaders, ...standaloneLinks] as any[]).map(link => <NavLink key={link.id} href={link.url} className="flex px-6 py-2 text-[13px] text-gray-500 hover:text-[#037a6a] transition-colors" onClick={() => {
                sendGAEvent("event", "navigation_item_clicked", {
                  item_name: link.title,
                  item_id: String(link.id)
                });
                setIsOpen(false);
              }}>{link.title}</NavLink>)}</div>}</div><div className="bg-white overflow-y-auto no-scrollbar relative transition-all duration-300 ease-in-out p-8" style={{
            width: `${dynamicRightWidth}px`
          }}><div key={`pane-${item.id}-${currentSubItem?.id}`} className="h-full w-full">{currentChildren.length > 0 ? <div className={`grid gap-x-10 gap-y-1 w-full ${columnCount === 3 ? "grid-cols-3" : columnCount === 2 ? "grid-cols-2" : "grid-cols-1"}`}>{currentChildren.map(child => <NavLink key={child.id} href={child.url || "#"} className={`relative font-outfit text-[15px] py-3.5 px-6 text-gray-700 hover:text-[#037a6a] hover:bg-gray-50/80 transition-all border-b border-gray-50 last:border-none flex items-center justify-between group/child w-[240px] ${String(child.id).startsWith("all-") ? "font-bold" : ""}`} onClick={() => {
                  sendGAEvent("event", "navigation_item_clicked", {
                    item_name: child.heading || child.title,
                    item_id: String(child.id)
                  });
                  setIsOpen(false);
                }}><span className="group-hover/child:font-bold transition-all">{child.heading || child.title}</span><ChevronRight size={14} className="opacity-0 group-hover/child:opacity-100 transition-all -translate-x-2 group-hover/child:translate-x-0 flex-shrink-0" /><div className="absolute left-0 top-2 bottom-2 w-1 bg-[#037a6a] scale-y-0 group-hover/child:scale-y-100 transition-transform duration-200" /></NavLink>)}</div> : <div className="h-full flex items-center justify-center w-full"><PromoCard currentSubItem={currentSubItem} isFullWidth={true} onLinkClick={() => setIsOpen(false)} /></div>}</div></div></></div></div>;
    $[37] = activeSubId;
    $[38] = allSidebarItems;
    $[39] = columnCount;
    $[40] = currentChildren;
    $[41] = currentSubItem;
    $[42] = dynamicRightWidth;
    $[43] = isOpen;
    $[44] = item.id;
    $[45] = itemIndex;
    $[46] = shouldShowDropdown;
    $[47] = standaloneHeaders;
    $[48] = standaloneLinks;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== t11 || $[51] !== t16 || $[52] !== t17) {
    t18 = <li className="relative cursor-pointer inline-block group px-6" onMouseEnter={t11} onMouseLeave={t12}>{t16}{t17}</li>;
    $[50] = t11;
    $[51] = t16;
    $[52] = t17;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  return t18;
});
NavMenuItem.displayName = "NavMenuItem";
const NavigationMenu: React.FC<NavigationMenuProps> = t0 => {
  const $ = _c(6);
  const {
    navigation
  } = t0;
  let t1;
  if ($[0] !== navigation) {
    t1 = Array.isArray(navigation) ? navigation : [];
    $[0] = navigation;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const safeNavigation = t1;
  let t2;
  if ($[2] !== safeNavigation) {
    t2 = safeNavigation.map((item, index) => <NavMenuItem key={`${item.id}-${index}`} item={item} navigation={safeNavigation} />);
    $[2] = safeNavigation;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <div className="flex xl:px-24 lg:px-16 justify-center lg:border-t border-stone-200/50 bg-white relative z-30"><ul className="md:mx-0 xl:mx-0 max-w-[1440px] mx-auto w-full hidden my-0 list-none py-3 gap-x-0 lg:flex items-center lg:justify-center">{t2}</ul></div>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
export default NavigationMenu;
function _temp(cat) {
  return cat.items.map(subItem => ({
    ...subItem,
    categoryTitle: cat.title,
    categoryType: cat.type
  }));
}
function _temp2(prev) {
  return !prev;
}